import React, {useState, useEffect} from 'react';
import Avatar from 'react-avatar';
import * as yup from 'yup';

import {postComment} from '../api/api';
import RichTextEditor from './RichTextEditor';
import {useAppContext} from '../hooks';
import Link from './Link';
import {cx} from '../util';

export const commentSchema = yup.object().shape({
  text: yup.string().min(40, 'Comments should be substantive. Is there more you can add?'),
});

// Create a unique draft key for every draft comment, including comment replies
const getDraftKey = (questionId, parentId) => `draftComment-${questionId}${parentId ? `-${parentId}` : ''}`;

// Synchronously read any saved markdown draft for this editor (client-only).
const readDraft = (questionId, parentId) => {
  if (typeof window === 'undefined') return '';
  const draft = JSON.parse(localStorage.getItem(getDraftKey(questionId, parentId)) || 'null');
  return draft?.data || '';
};

const ClosedCommentEditor = ({onOpen, placeholderText}) => {
  const {currentUser} = useAppContext();

  return (
    <button type="button" onClick={onOpen}>
      <div className="flex items-center p-5">
        <Avatar
          className="bg-gray-100 size-10 mr-4 rounded-full shrink-0"
          name={currentUser?.displayName}
          src={currentUser?.profile_img}
          color="#f0f0f0"
          fgColor="#000000"
          size={40}
        />
        <div className="text-base text-gray-400">
          {placeholderText}
        </div>
      </div>
    </button>
  );
};

const OpenCommentEditor = ({
  questionID,
  parentCommentID,
  onSubmit,
  placeholderText,
  showToolbar,
}) => {
  const [initialMarkdown] = useState(() => readDraft(questionID, parentCommentID));
  const [text, setText] = useState(initialMarkdown);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const {currentUser} = useAppContext();

  const onEditorChange = (markdown) => {
    setText(markdown);
    setError(null);
    localStorage.setItem(
      getDraftKey(questionID, parentCommentID),
      JSON.stringify({time: Date.now(), data: markdown}),
    );
  };

  const handleSubmit = async (e) => {
    try {
      e.preventDefault();
      setSubmitting(true);
      const comment = {text};
      if (!parentCommentID) await commentSchema.validate(comment);
      await postComment(questionID, parentCommentID, comment);

      const draftKey = getDraftKey(questionID, parentCommentID);
      localStorage.removeItem(draftKey);

      if (onSubmit) {
        onSubmit();
      } else {
        window.location.reload();
      }
    } catch (err) {
      setError(err);
    }

    setSubmitting(false);
    return false;
  };

  return (
    <>
      <div className="flex items-center pt-5 px-5">
        <Avatar
          className="bg-gray-100 size-10 mr-4 rounded-full shrink-0"
          name={currentUser?.displayName}
          src={currentUser?.profile_img}
          color="#f0f0f0"
          fgColor="#000000"
          size={40}
        />
        <div className="font-semibold text-sm">{currentUser?.displayName}</div>
      </div>
      {currentUser ? (
        <RichTextEditor
          initialMarkdown={initialMarkdown}
          onChange={onEditorChange}
          showToolbar={showToolbar}
          placeholderText={placeholderText}
          onSubmit={handleSubmit}
          submitting={submitting}
        />
      ) : (
        <div>Login to comment</div>
      )}
      {error && (
        <div className="text-right text-red-600 text-sm pt-2 pb-4 px-4">
          {error.message}
        </div>
      )}
    </>
  );
};

export const CommentEditor = ({
  onOpen,
  isOpen = false,
  showToolbar = true,
  placeholderText = 'Add your own answer to this question...',
  questionID,
  parentCommentID,
  onSubmit,
  className,
}) => {
  const [open, setOpen] = useState(isOpen);

  useEffect(() => {
    const draftKey = getDraftKey(questionID, parentCommentID);
    const localStorageDraft = JSON.parse(localStorage.getItem(draftKey));
    if (localStorageDraft?.data) setOpen(true);
  }, []);

  useEffect(() => {
    if (isOpen) {
      setOpen(true);
    }
  }, [isOpen]);

  const openEditor = () => {
    onOpen();
    setOpen(true);
  };

  return (
    <div className={cx('border border-gray-200 rounded-lg', className)}>
      {open ? (
        <OpenCommentEditor
          questionID={questionID}
          parentCommentID={parentCommentID}
          onSubmit={onSubmit}
          placeholderText={placeholderText}
          showToolbar={showToolbar}
        />
      ) : (
        <ClosedCommentEditor
          onOpen={openEditor}
          placeholderText={placeholderText}
        />
      )}
    </div>
  );
};

// Wrapper component mainly for extra ui components or
// special styling applied to the container if needed
export const ReplyEditor = ({
  questionID, parentCommentID, onSubmit, submitted,
}) => (submitted ? (
  <div className="p-4">
    Reply submitted! See the whole conversation
    {' '}
    <Link href={`/questions/${questionID}`} className="text-indigo-700">
      here
    </Link>
  </div>
) : (
  <div className="border-t border-gray-200">
    <OpenCommentEditor
      questionID={questionID}
      parentCommentID={parentCommentID}
      onSubmit={onSubmit}
      placeholderText="Add your reply..."
      showToolbar={false}
    />
  </div>
)
);

